import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip } from 'recharts';
import { Activity, AlertTriangle, Shield, PieChart as PieIcon, TrendingUp } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import AppLayout from '../components/AppLayout';

const COLORS = ['#1A4D2E', '#C1F23D', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#6366F1'];

const AnalyticsPage = () => {
  const { api, user } = useAuth();
  const [metrics, setMetrics] = useState(null);
  const [holdings, setHoldings] = useState([]);
  const [correlation, setCorrelation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    loadAnalytics();
  }, []);

  const loadAnalytics = async () => {
    setLoading(true);
    try {
      const [riskRes, holdingsRes] = await Promise.all([
        api.get('/analytics/risk'),
        api.get('/analytics/holdings')
      ]);
      setMetrics(riskRes.data);
      setHoldings(holdingsRes.data);
    } catch (error) {
      console.error('Failed to load analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatPercent = (v) => `${(v || 0).toFixed(1)}%`;
  const formatCurrency = (v) => `$${(v || 0).toLocaleString()}`;

  if (loading) {
    return (
      <AppLayout>
        <div className="max-w-6xl mx-auto px-4 py-12 text-center">
          <div className="animate-pulse text-[#1A4D2E]">Loading analytics...</div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="max-w-6xl mx-auto px-4 py-8">
        <h1 className="font-serif text-3xl text-[#1F2937] mb-8">Portfolio Analytics</h1>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="holdings">Holdings</TabsTrigger>
            <TabsTrigger value="risk">Risk Metrics</TabsTrigger>
          </TabsList>

          <TabsContent value="overview">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center gap-3 mb-2">
                    <Activity className="w-5 h-5 text-blue-600" />
                    <span className="text-sm text-gray-600">Portfolio Beta</span>
                  </div>
                  <p className="text-3xl font-bold text-[#1F2937]">
                    {metrics?.portfolioBeta?.toFixed(2) || '—'}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    {metrics?.portfolioBeta > 1 ? 'More volatile than market' : 'Less volatile than market'}
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center gap-3 mb-2">
                    <AlertTriangle className="w-5 h-5 text-red-600" />
                    <span className="text-sm text-gray-600">VaR (95%)</span>
                  </div>
                  <p className="text-3xl font-bold text-red-600">
                    -{formatCurrency(metrics?.var95 || 0)}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">Max daily loss expected</p>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center gap-3 mb-2">
                    <Shield className="w-5 h-5 text-green-600" />
                    <span className="text-sm text-gray-600">Diversification</span>
                  </div>
                  <p className="text-3xl font-bold text-[#1F2937]">
                    {formatPercent(metrics?.diversificationScore)}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    {metrics?.diversificationScore > 70 ? 'Well diversified' : 'Consider more variety'}
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center gap-3 mb-2">
                    <PieIcon className="w-5 h-5 text-purple-600" />
                    <span className="text-sm text-gray-600">Total Value</span>
                  </div>
                  <p className="text-3xl font-bold text-[#1F2937]">
                    {formatCurrency(metrics?.totalValue || 0)}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    {metrics?.positionsCount || 0} positions
                  </p>
                </CardContent>
              </Card>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <Card>
                <CardHeader>
                  <CardTitle>Sector Allocation</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-80">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={metrics?.sectorAllocation || []}
                          dataKey="value"
                          nameKey="sector"
                          cx="50%"
                          cy="50%"
                          outerRadius={120}
                          label={({ sector, percentage }) => `${sector} (${percentage.toFixed(0)}%)`}
                        >
                          {(metrics?.sectorAllocation || []).map((entry, index) => (
                            <Cell key={index} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(v) => formatCurrency(v)} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Holdings Breakdown</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-80">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={holdings.slice(0, 10)} layout="vertical">
                        <XAxis type="number" tickFormatter={formatCurrency} />
                        <YAxis dataKey="symbol" type="category" width={60} />
                        <Tooltip formatter={formatCurrency} />
                        <Bar dataKey="marketValue" fill="#1A4D2E" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="holdings">
            <Card>
              <CardHeader>
                <CardTitle>All Holdings</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-stone-200">
                        <th className="text-left py-3 text-sm font-medium text-gray-600">Symbol</th>
                        <th className="text-left py-3 text-sm font-medium text-gray-600">Shares</th>
                        <th className="text-left py-3 text-sm font-medium text-gray-600">Avg Cost</th>
                        <th className="text-left py-3 text-sm font-medium text-gray-600">Current</th>
                        <th className="text-left py-3 text-sm font-medium text-gray-600">Value</th>
                        <th className="text-left py-3 text-sm font-medium text-gray-600">Return</th>
                        <th className="text-left py-3 text-sm font-medium text-gray-600">Weight</th>
                      </tr>
                    </thead>
                    <tbody>
                      {holdings.map((h) => (
                        <tr key={h.symbol} className="border-b border-stone-100">
                          <td className="py-3 font-semibold">{h.symbol}</td>
                          <td className="py-3">{h.shares.toFixed(2)}</td>
                          <td className="py-3">{formatCurrency(h.avgCost)}</td>
                          <td className="py-3">{formatCurrency(h.currentPrice)}</td>
                          <td className="py-3 font-medium">{formatCurrency(h.marketValue)}</td>
                          <td className={`py-3 ${h.gain >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {formatPercent(h.gainPercent)}
                          </td>
                          <td className="py-3">{formatPercent(h.weight)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="risk">
            <Card>
              <CardHeader>
                <CardTitle>Risk Analysis</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="bg-stone-50 p-6 rounded-lg">
                    <h3 className="font-semibold text-[#1F2937] mb-4">Understanding Your Risk</h3>
                    <div className="space-y-4">
                      <div>
                        <p className="text-sm text-gray-600">Beta</p>
                        <p className="text-lg font-medium">
                          {metrics?.portfolioBeta?.toFixed(2) || '—'}
                        </p>
                        <p className="text-xs text-gray-500">
                          Measures volatility relative to market. &gt;1 means more volatile.
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Value at Risk (95%)</p>
                        <p className="text-lg font-medium text-red-600">
                          -{formatCurrency(metrics?.var95 || 0)}
                        </p>
                        <p className="text-xs text-gray-500">
                          Maximum expected daily loss with 95% confidence.
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Diversification Score</p>
                        <p className="text-lg font-medium">
                          {formatPercent(metrics?.diversificationScore)}
                        </p>
                        <p className="text-xs text-gray-500">
                          Higher score = better diversification across sectors.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="md:col-span-2 bg-blue-50 p-6 rounded-lg">
                    <h3 className="font-semibold text-[#1F2937] mb-4">
                      <TrendingUp className="w-5 h-5 inline mr-2" />
                      Tips to Improve Your Portfolio
                    </h3>
                    <ul className="space-y-3 text-sm text-gray-700">
                      {metrics?.portfolioBeta > 1.2 && (
                        <li>• Consider reducing high-beta stocks to lower volatility</li>
                      )}
                      {metrics?.diversificationScore < 50 && (
                        <li>• Add positions from different sectors to improve diversification</li>
                      )}
                      {metrics?.positionsCount < 5 && (
                        <li>• Add more positions to reduce concentration risk</li>
                      )}
                      <li>• Review your largest positions and consider rebalancing</li>
                      <li>• Monitor your portfolio's correlation to avoid overexposure</li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </AppLayout>
  );
};

export default AnalyticsPage;
